import React, { useEffect, useState, useMemo } from "react";
import { api, BRAND } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { ArrowUpRight, X, MapPin, Maximize2, BedDouble, Bath, FileDown, MessageCircle } from "lucide-react";

const FILTERS = [
  { key: "all", i18n: "filter_all" },
  { key: "in_development", i18n: "filter_in_development" },
  { key: "for_sale", i18n: "filter_for_sale" },
];

// Merge i18n[lang] overrides on top of the base project fields
const localize = (project, lang) => {
  if (!project) return project;
  const override = project.i18n && project.i18n[lang];
  return override ? { ...project, ...override } : project;
};

const ProjectCard = ({ project, onOpen, t, idx, lang }) => {
  const p = localize(project, lang);
  const statusKey = `status_${p.status}`;
  return (
    <button
      type="button"
      onClick={() => onOpen(project)}
      data-testid={`project-card-${project.id}`}
      className="group text-left block w-full"
    >
      <div className="relative aspect-[4/5] w-full overflow-hidden bg-sand">
        <img
          src={p.cover_image}
          alt={p.title}
          className="w-full h-full object-cover transition-transform duration-[1800ms] ease-out group-hover:scale-[1.06]"
          loading="lazy"
        />
        <div className={`absolute top-5 left-5 backdrop-blur-sm px-3 py-1.5 text-[0.65rem] tracking-[0.22em] uppercase font-medium ${p.status === "sold" ? "bg-charcoal text-ivory" : "bg-ivory/95 text-charcoal"}`}>
          {t(`projects.${statusKey}`)}
        </div>
        {typeof p.units_available === "number" && p.units_available > 0 && (
          <div
            className="absolute top-5 right-5 bg-teakwood text-ivory px-3 py-1.5 text-[0.65rem] tracking-[0.22em] uppercase font-medium shadow-sm"
            data-testid={`project-availability-${project.id}`}
          >
            {t("projects.only")} {p.units_available} {t("projects.available_short")}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="absolute bottom-5 right-5 w-11 h-11 rounded-full bg-ivory text-charcoal flex items-center justify-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
          <ArrowUpRight size={18} />
        </div>
      </div>
      <div className="mt-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="overline">
            {String(idx + 1).padStart(2, "0")} · {p.location}
          </div>
          <h3 className="mt-2 font-heading text-2xl sm:text-3xl font-normal tracking-tight leading-tight text-charcoal">
            {p.title}
          </h3>
          {p.subtitle && (
            <p className="mt-2 text-[0.95rem] text-volcanic font-light leading-relaxed">
              {p.subtitle}
            </p>
          )}
        </div>
        {(p.price || p.price_note) && (
          <div className="text-right shrink-0">
            {p.price_note && (
              <div className={`tracking-[0.22em] uppercase font-medium ${p.price ? "text-[0.65rem] text-volcanic/70" : "text-sm text-charcoal"}`}>
                {p.price_note}
              </div>
            )}
            {p.price && (
              <div className="mt-1 font-heading text-2xl sm:text-3xl text-charcoal tracking-tight leading-none">
                {p.price}
              </div>
            )}
          </div>
        )}
      </div>
    </button>
  );
};

const ProjectModal = ({ project, onClose, t, lang }) => {
  const p = project ? localize(project, lang) : null;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!project) return null;
  const statusKey = `status_${p.status}`;
  const waInfo = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(
    t("projects.request_info_message").replace("{title}", p.title)
  )}`;

  return (
    <div
      className="fixed inset-0 z-[60] overflow-y-auto bg-charcoal/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="project-modal"
    >
      <div
        className="min-h-screen flex items-start justify-center p-4 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative w-full max-w-5xl bg-ivory">
          <button
            onClick={onClose}
            data-testid="project-modal-close"
            className="absolute top-4 right-4 z-10 w-11 h-11 bg-ivory/90 hover:bg-charcoal hover:text-ivory text-charcoal flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>

          <div className="aspect-[16/10] w-full overflow-hidden bg-sand">
            <img
              src={p.cover_image}
              alt={p.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="p-6 sm:p-12">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="bg-charcoal text-ivory px-3 py-1.5 text-[0.65rem] tracking-[0.22em] uppercase font-medium">
                {t(`projects.${statusKey}`)}
              </span>
              <span className="overline flex items-center gap-2">
                <MapPin size={12} /> {p.location}
              </span>
            </div>
            <h2 className="mt-6 font-heading font-light text-4xl sm:text-5xl tracking-tight leading-[1.05] text-charcoal">
              {p.title}
            </h2>
            {p.subtitle && (
              <p className="mt-3 text-lg text-volcanic font-light">{p.subtitle}</p>
            )}

            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="md:col-span-2">
                <p className="text-base leading-[1.8] text-volcanic font-light whitespace-pre-line">
                  {p.description}
                </p>

                {p.features && p.features.length > 0 && (
                  <div className="mt-10">
                    <div className="overline">{t("projects.features")}</div>
                    <ul className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3">
                      {p.features.map((f) => (
                        <li
                          key={f}
                          className="flex items-center gap-3 text-sm text-charcoal"
                        >
                          <span className="h-px w-5 bg-gold" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="space-y-6 border-t md:border-t-0 md:border-l border-charcoal/10 md:pl-8 pt-8 md:pt-0">
                {(p.price || p.price_note) && (
                  <div>
                    {p.price_note && (
                      <div className={`tracking-[0.22em] uppercase font-medium ${p.price ? "text-[0.65rem] text-volcanic/70" : "text-base text-charcoal"}`}>
                        {p.price_note}
                      </div>
                    )}
                    {p.price && (
                      <div className="mt-1 font-heading text-3xl sm:text-4xl text-charcoal tracking-tight leading-none">
                        {p.price}
                      </div>
                    )}
                  </div>
                )}

                {typeof p.units_available === "number" &&
                  p.units_available > 0 && (
                    <div
                      className="bg-teakwood/10 border-l-2 border-teakwood px-4 py-3"
                      data-testid="modal-availability"
                    >
                      <div className="text-[0.65rem] tracking-[0.22em] uppercase text-teakwood font-semibold">
                        {t("projects.limited_availability")}
                      </div>
                      <div className="mt-1 font-heading text-xl text-charcoal">
                        {t("projects.only")} {p.units_available}{" "}
                        {t("projects.available")}
                        {typeof p.units_total === "number" && (
                          <span className="text-volcanic/60 text-sm ml-2">
                            / {p.units_total}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                {p.area && (
                  <div>
                    <div className="overline flex items-center gap-2">
                      <Maximize2 size={12} /> {t("projects.area")}
                    </div>
                    <div className="mt-1 font-heading text-xl text-charcoal">{p.area}</div>
                  </div>
                )}
                {p.bedrooms != null && (
                  <div>
                    <div className="overline flex items-center gap-2">
                      <BedDouble size={12} /> {t("projects.bedrooms")}
                    </div>
                    <div className="mt-1 font-heading text-xl text-charcoal">
                      {p.bedrooms}
                    </div>
                  </div>
                )}
                {p.bathrooms != null && (
                  <div>
                    <div className="overline flex items-center gap-2">
                      <Bath size={12} /> {t("projects.bathrooms")}
                    </div>
                    <div className="mt-1 font-heading text-xl text-charcoal">
                      {p.bathrooms}
                    </div>
                  </div>
                )}

                <a
                  href={waInfo}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="project-modal-request-info"
                  className="mt-4 inline-flex w-full justify-center items-center gap-2 bg-charcoal text-ivory px-6 py-4 text-[0.72rem] tracking-[0.22em] uppercase font-medium hover:bg-teakwood transition-colors duration-500"
                >
                  <MessageCircle size={14} />
                  {t("projects.request_info")}
                </a>
              </div>
            </div>

            {/* Payment plan intentionally hidden from the public modal.
                Data is kept on the project for future use (e.g. inside the dossier). */}
            {false && p.payment_plan && p.payment_plan.length > 0 && (
              <div
                className="mt-16 bg-sand/40 p-6 sm:p-10"
                data-testid="payment-plan"
              >
                <div className="flex items-baseline justify-between flex-wrap gap-3">
                  <div>
                    <div className="overline">— {t("projects.payment_plan")}</div>
                    <h3 className="mt-3 font-heading font-light text-3xl sm:text-4xl tracking-tight text-charcoal leading-tight">
                      {t("projects.payment_plan_title")}
                    </h3>
                  </div>
                  <p className="text-sm text-volcanic font-light max-w-md">
                    {t("projects.payment_plan_sub")}
                  </p>
                </div>

                <div className="mt-10 relative">
                  {/* Timeline line */}
                  <div className="hidden md:block absolute top-5 left-0 right-0 h-px bg-charcoal/15" />
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-4">
                    {p.payment_plan.map((stage, i) => (
                      <div
                        key={`${stage.label}-${i}`}
                        className="relative"
                        data-testid={`payment-stage-${i}`}
                      >
                        {/* Dot */}
                        <div className="hidden md:block absolute top-[18px] left-0 w-[10px] h-[10px] rounded-full bg-teakwood ring-4 ring-sand/40" />
                        <div className="md:pl-5">
                          <div className="overline text-teakwood">{stage.label}</div>
                          <div className="mt-2 font-heading text-3xl sm:text-4xl font-light tracking-tight text-charcoal leading-none">
                            {stage.percentage}
                            <span className="text-2xl align-top">%</span>
                          </div>
                          {stage.amount && (
                            <div className="mt-2 font-heading text-lg text-charcoal">
                              {stage.amount}
                            </div>
                          )}
                          {stage.milestone && (
                            <p className="mt-2 text-[0.9rem] leading-snug text-volcanic font-light">
                              {stage.milestone}
                            </p>
                          )}
                          {stage.when && (
                            <div className="mt-2 text-[0.7rem] tracking-[0.18em] uppercase text-volcanic/60 font-medium">
                              {stage.when}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="mt-10 text-xs text-volcanic/70 italic">
                  {t("projects.payment_plan_note")}
                </p>
              </div>
            )}

            {p.gallery && p.gallery.length > 1 && (
              <div className="mt-16">
                <div className="overline">{t("projects.gallery")}</div>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {p.gallery.map((src, i) => (
                    <div key={`${src}-${i}`} className="aspect-[4/3] overflow-hidden bg-sand">
                      <img
                        src={src}
                        alt={`${p.title} ${i + 1}`}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1500ms]"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Projects = () => {
  const { t, lang } = useLang();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/projects");
        if (mounted) setProjects(data);
      } catch (e) {
        console.error(e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return projects;
    if (filter === "in_development") {
      return projects.filter(
        (p) => p.status === "in_development" || p.also_in_development === true
      );
    }
    return projects.filter((p) => p.status === filter);
  }, [projects, filter]);

  return (
    <section id="projects" data-testid="projects-section" className="bg-ivory">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10 pt-24 sm:pt-32 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <p className="overline" data-testid="projects-overline">— {t("projects.overline")}</p>
            <h2
              className="mt-6 font-heading font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-charcoal whitespace-pre-line"
              data-testid="projects-title"
            >
              {t("projects.title")}
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <p className="text-base sm:text-lg text-volcanic font-light leading-relaxed">
              {t("projects.subtitle")}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div
          className="mt-16 flex flex-wrap items-center gap-3 border-t border-charcoal/10 pt-6"
          data-testid="projects-filters"
        >
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}`}
              className={`px-5 py-2.5 text-[0.72rem] tracking-[0.22em] uppercase font-medium border transition-colors duration-300 ${
                filter === f.key
                  ? "bg-charcoal text-ivory border-charcoal"
                  : "bg-transparent text-charcoal border-charcoal/20 hover:border-charcoal"
              }`}
            >
              {t(`projects.${f.i18n}`)}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="mt-14">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aspect-[4/5] bg-sand/80 animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div
              className="py-24 text-center text-volcanic font-light"
              data-testid="projects-empty"
            >
              {t("projects.empty")}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
              {filtered.map((proj, idx) => (
                <ProjectCard
                  key={proj.id}
                  project={proj}
                  idx={idx}
                  onOpen={setSelected}
                  t={t}
                  lang={lang}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {selected && (
        <ProjectModal project={selected} onClose={() => setSelected(null)} t={t} lang={lang} />
      )}
    </section>
  );
};

export default Projects;
